// staff-eval-data.js - the Trainer & Coach Evaluation rubric (Module 6),
// transcribed verbatim from KHC_Dragons_Evaluation_Form.docx (2026-06-11).
// Criteria live here as config so wording changes never need a migration -
// the DB stores only criterion keys (V6_DESIGN §11.4).
//
// Ratings are stored 1-5 (+ NULL for N/A); the five display words match the
// paper form. Part D (self-reflection) and Part E (overall) are free-text
// columns on staff_evaluations, not criteria.

// Stored value -> display word. Order matters for the rating buttons
// (best first, like the paper form's scale table).
export const STAFF_RATING_LEVELS = [
  { value: 5, word: 'Outstanding',          abbr: 'OUT' },
  { value: 4, word: 'Exceeds Expectations', abbr: 'EXC' },
  { value: 3, word: 'Meets Expectations',   abbr: 'MEE' },
  { value: 2, word: 'Developing',           abbr: 'DEV' },
  { value: 1, word: 'Needs Improvement',    abbr: 'IMP' },
];

export const STAFF_RATING_MEANINGS = {
  5: 'A genuine strength and a model for others; consistently sets the standard within the club.',
  4: 'Regularly performs above what the role requires; a clear positive.',
  3: 'Reliably does what the role asks; solid, dependable performance.',
  2: 'On the right track but not yet consistent; growing into this area.',
  1: 'A clear gap that should be actively worked on; support required.',
};

// part: 'A' applies always; 'B' only when role includes trainer;
// 'C' only when role includes coach.
export const STAFF_EVAL_SECTIONS = [
  {
    part: 'A', key: 'a1', title: 'Communication & people skills',
    criteria: [
      { key: 'a1_respectful',  label: 'Communicates respectfully with players' },
      { key: 'a1_motivation',  label: 'Builds motivation and self-confidence' },
      { key: 'a1_feedback',    label: 'Gives constructive, usable feedback' },
      { key: 'a1_parents',     label: 'Communicates professionally with parents (youth)' },
      { key: 'a1_staff',       label: 'Works well with staff and the wider club' },
    ],
  },
  {
    part: 'A', key: 'a2', title: 'Player development',
    criteria: [
      { key: 'a2_individual',  label: 'Attends to individual development, not just the group' },
      { key: 'a2_independence',label: 'Encourages independence and responsibility' },
      { key: 'a2_tracking',    label: 'Tracks and follows up on player progress' },
      { key: 'a2_mental',      label: 'Gives attention to mental / personal development' },
      { key: 'a2_safe',        label: 'Creates a safe learning environment' },
    ],
  },
  {
    part: 'A', key: 'a3', title: 'Attitude, professionalism & administration',
    criteria: [
      { key: 'a3_punctual',    label: 'Punctual and reliable' },
      { key: 'a3_example',     label: 'Sets a good example through own behaviour' },
      { key: 'a3_open',        label: 'Open to feedback and reflects on own practice' },
      { key: 'a3_admin',       label: 'Keeps club administration up to date (Twizzit, match sheets, attendance)' },
      { key: 'a3_responsive',  label: 'Responds to club and parent communications in good time' },
      { key: 'a3_commitment',  label: 'Shows commitment within the club' },
      { key: 'a3_develops',    label: 'Keeps developing as a trainer / coach' },
    ],
  },
  {
    part: 'A', key: 'a4', title: 'Team culture & club values',
    criteria: [
      { key: 'a4_fairplay',    label: 'Promotes respect and fair play' },
      { key: 'a4_atmosphere',  label: 'Builds a positive team atmosphere' },
      { key: 'a4_discipline',  label: 'Encourages discipline and effort' },
      { key: 'a4_inclusive',   label: 'Fosters an inclusive team' },
      // Was 'KHC Dragons values'. This form is transcribed from the Dragons
      // paper original, but it is the form EVERY club's coaches are assessed
      // against, so the one line naming a club had to go. The club's own
      // values are what 'the club' means to whoever is filling it in.
      { key: 'a4_values',      label: 'Actively carries out the club values' },
    ],
  },
  {
    part: 'B', key: 'b5', title: 'Training vision & preparation',
    criteria: [
      { key: 'b5_vision',      label: 'Has a clear training vision' },
      { key: 'b5_prepared',    label: 'Sessions are well prepared' },
      { key: 'b5_aligned',     label: "Training aligns with the club's playing vision" },
      { key: 'b5_differentiates', label: 'Differentiates by level and player profile' },
      { key: 'b5_floor',       label: 'Manages the training floor and uses time efficiently' },
    ],
  },
  {
    part: 'B', key: 'b6', title: 'Technical & tactical instruction',
    criteria: [
      { key: 'b6_technical',   label: 'Teaches technical skills correctly' },
      { key: 'b6_tactical',    label: 'Tactical principles are explained clearly' },
      { key: 'b6_instructions',label: 'Gives clear instructions during training' },
      { key: 'b6_improves',    label: 'Builds sessions that genuinely improve players' },
      { key: 'b6_adjusts',     label: 'Adjusts drills and load to the group in front of them' },
      { key: 'b6_safety',      label: 'Manages session safety (warm-up, equipment, pitch, injuries)' },
    ],
  },
  {
    part: 'C', key: 'c7', title: 'Match preparation & selection',
    criteria: [
      { key: 'c7_structured',  label: 'Match preparation is structured' },
      { key: 'c7_selection',   label: 'Makes fair, well-reasoned selection decisions' },
      { key: 'c7_playtime',    label: "Manages playing time appropriately for the team's level" },
      { key: 'c7_tactical',    label: 'Prepares the team tactically for the opponent' },
      { key: 'c7_gameplan',    label: 'Communicates roles and the game plan clearly' },
    ],
  },
  {
    part: 'C', key: 'c8', title: 'Match coaching',
    criteria: [
      { key: 'c8_positive',    label: 'Match coaching is positive and goal-directed' },
      { key: 'c8_reads',       label: 'Reads match situations and adjusts in-game' },
      { key: 'c8_subs',        label: 'Manages substitutions and game flow effectively' },
      { key: 'c8_composed',    label: 'Stays composed and constructive under pressure' },
      { key: 'c8_reviews',     label: 'Reviews the match with players afterwards' },
    ],
  },
];

/** Sections that apply to a given role_assessed ('trainer'|'coach'|'both'). */
export function sectionsForRole(role) {
  return STAFF_EVAL_SECTIONS.filter(s =>
    s.part === 'A' ||
    (s.part === 'B' && (role === 'trainer' || role === 'both')) ||
    (s.part === 'C' && (role === 'coach'   || role === 'both'))
  );
}

/** Display word for a stored rating (null/undefined = N/A). */
export function ratingWord(value) {
  if (value == null) return 'N/A';
  return STAFF_RATING_LEVELS.find(l => l.value === value)?.word || String(value);
}

/** Flat list of every criterion key for a role - used for progress counts. */
export function criteriaForRole(role) {
  return sectionsForRole(role).flatMap(s => s.criteria.map(c => c.key));
}
